import {
  useV2CreateLanguageFieldMutation,
  useV2UpdateLanguageFieldMutation,
  useV2DeleteLanguageFieldMutation,
  useV2ChangeLanguageFieldPositionByIdMutation,
} from "@generated";
import type { V2LanguageFieldResource } from "@generated";
import type { LanguageFieldFormValues } from "../types";

// The `account` relationship is inferred from auth server-side, so we omit it
// and cast (same pattern as the Currencies/LinesOfBusiness MSPAs). `sow-language`
// is server-derived from the name (Slugifier) and immutable, so it is never
// written — same as the TextBlocks slug.
export const buildLanguageFieldData = (
  values: LanguageFieldFormValues,
  id?: string
): V2LanguageFieldResource =>
  ({
    type: "language-fields",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      preamble: values.preamble,
      postamble: values.postamble,
    },
  } as unknown as V2LanguageFieldResource);

// Reorder via the dedicated /moves endpoint (acts_as_list set_list_position),
// same shape as the line-of-business move.
const movePositionData = (
  id: string,
  position: number
): V2LanguageFieldResource =>
  ({
    type: "language-fields",
    id: id,
    attributes: { position },
  } as unknown as V2LanguageFieldResource);

export const useLanguageFieldMutations = () => {
  const [create] = useV2CreateLanguageFieldMutation();
  const [update] = useV2UpdateLanguageFieldMutation();
  const [destroy] = useV2DeleteLanguageFieldMutation();
  const [move] = useV2ChangeLanguageFieldPositionByIdMutation();

  return {
    create: (values: LanguageFieldFormValues) =>
      create({ body: { data: buildLanguageFieldData(values) } }).unwrap(),
    update: (id: string, values: LanguageFieldFormValues) =>
      update({
        id: id,
        body: { data: buildLanguageFieldData(values, id) },
      }).unwrap(),
    archive: (id: string) => destroy({ id: id }).unwrap(),
    move: (id: string, position: number) =>
      move({
        id: id,
        body: { data: movePositionData(id, position) },
      }).unwrap(),
    restore: (id: string, name: string) =>
      update({
        id: id,
        body: {
          data: {
            type: "language-fields",
            id: id,
            attributes: { name, active: true },
          } as unknown as V2LanguageFieldResource,
        },
      }).unwrap(),
  };
};

export default useLanguageFieldMutations;
